import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Loader2, ImagePlus, Trash2, Eye } from "lucide-react";

const Frame = ({ html, label, testId }) => (
  <div>
    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="relative w-full overflow-hidden rounded-md border border-slate-200 shadow-sm" style={{ aspectRatio: "3 / 2" }}>
      <iframe title={label} srcDoc={html} sandbox="" data-testid={testId} className="absolute left-0 top-0 origin-top-left border-0"
        style={{ width: "576px", height: "384px" }} ref={(el) => { if (el) el.style.transform = `scale(${el.parentElement.clientWidth / 576})`; }} />
    </div>
  </div>
);

function StyleCard({ style, set }) {
  const onLogo = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 300_000) { toast.error("Logo must be under 300 KB"); return; }
    const r = new FileReader(); r.onload = () => set({ ...style, logo: r.result }); r.readAsDataURL(f);
  };
  return (
    <Card className="p-5">
      <h3 className="mb-3 font-display font-bold text-slate-900">Brand (all postcards)</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[["primary", "Primary color"], ["accent", "Accent color"]].map(([k, l]) => (
          <label key={k} className="text-xs font-semibold text-slate-600">{l}
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={style[k]} onChange={(e) => set({ ...style, [k]: e.target.value })} className="h-9 w-12 cursor-pointer rounded border border-slate-200" data-testid={`pc-${k}-color`} />
              <Input value={style[k]} onChange={(e) => set({ ...style, [k]: e.target.value })} className="font-mono" data-testid={`pc-${k}-hex`} />
            </div>
          </label>
        ))}
        <div className="text-xs font-semibold text-slate-600">Company logo (front)
          <div className="mt-1 flex items-center gap-2">
            {style.logo && <img src={style.logo} alt="logo" className="h-9 max-w-[90px] rounded border border-slate-200 bg-slate-700 object-contain p-1" data-testid="pc-logo-preview" />}
            <label className="inline-flex cursor-pointer items-center rounded-md border border-slate-200 px-3 py-2 text-xs hover:bg-slate-50" data-testid="pc-logo-upload">
              <ImagePlus className="mr-1.5 h-4 w-4" /> Upload<input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onLogo} data-testid="pc-logo-input" />
            </label>
            {style.logo && <Button size="sm" variant="ghost" onClick={() => set({ ...style, logo: "" })} data-testid="pc-logo-remove"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>
      </div>
    </Card>
  );
}

function TypeEditor({ t, set }) {
  const f = (k) => (e) => set({ ...t, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Headline (front)</Label><Input value={t.headline} maxLength={70} onChange={f("headline")} data-testid="pc-headline" /></div>
      <div><Label className="text-xs">Tagline (front)</Label><Input value={t.tagline} maxLength={90} onChange={f("tagline")} data-testid="pc-tagline" /></div>
      <div><Label className="text-xs">Message (back)</Label><Textarea rows={3} value={t.message} maxLength={260} onChange={f("message")} data-testid="pc-message" /></div>
      <div className="rounded-lg border border-slate-200 p-3">
        <label className="flex items-center justify-between text-sm font-semibold text-slate-800">QR code<Switch checked={t.qr_enabled} onCheckedChange={(v) => set({ ...t, qr_enabled: v })} data-testid="pc-qr-enabled" /></label>
        {t.qr_enabled && (
          <div className="mt-3 space-y-3">
            <div><Label className="text-xs">QR caption</Label><Input value={t.qr_caption} maxLength={30} onChange={f("qr_caption")} data-testid="pc-qr-caption" /></div>
            <div className="flex flex-wrap gap-2">
              {[["response", "Elite Leads response page (tracks responses)"], ["custom", "Custom link"]].map(([m, l]) => (
                <button key={m} type="button" onClick={() => set({ ...t, qr_mode: m })} data-testid={`pc-qr-mode-${m}`}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${t.qr_mode === m ? "border-teal-700 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-600"}`}>{l}</button>
              ))}
            </div>
            {t.qr_mode === "custom" && <div><Label className="text-xs">Custom QR link (https://)</Label><Input value={t.qr_url} onChange={f("qr_url")} placeholder="https://eliterealestatefinance.com/apply" data-testid="pc-qr-url" />
              <p className="mt-1 text-[11px] text-amber-700">Custom links skip Elite Leads response tracking; the response code is still printed.</p></div>}
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-500">Compliance text (advertisement notice, NMLS, Equal Housing Lender, not a commitment to lend, opt-out, and reverse-mortgage HUD wording) is always added and can't be edited.</p>
    </div>
  );
}

export default function AdminPostcards() {
  const [design, setDesign] = useState(null);
  const [types, setTypes] = useState([]);
  const [tab, setTab] = useState("Purchase");
  const [pv, setPv] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/postcard-design").then((r) => { setDesign(r.data.design); setTypes(r.data.loan_types); }); }, []);
  const preview = useCallback(async () => {
    if (!design) return;
    try { setPv((await api.post("/admin/postcard-design/preview", { design, loan_type: tab })).data); } catch (e) { toast.error(apiError(e)); }
  }, [design, tab]);
  useEffect(() => { const h = setTimeout(preview, 400); return () => clearTimeout(h); }, [preview]);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/postcard-design", { design }); setDesign(data.design); toast.success("Postcard design saved"); }
    catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };
  if (!design) return <div className="p-10 text-center text-sm text-slate-400">Loading…</div>;
  return (
    <div>
      <PageHeader title="Postcard Designer" subtitle="Edit the postcard for each loan type: text, colors, logo and QR code. Changes apply to every postcard mailed after you save.">
        <Button onClick={save} disabled={saving} className="gold-cta border-0 font-bold" data-testid="pc-save-btn">{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save design</Button>
      </PageHeader>
      <div className="space-y-6">
        <StyleCard style={design.style} set={(s) => setDesign({ ...design, style: s })} />
        <Card className="p-5">
          <div className="mb-4 flex flex-wrap gap-2">
            {types.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`pc-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <TypeEditor key={tab} t={design.types[tab]} set={(v) => setDesign({ ...design, types: { ...design.types, [tab]: v } })} />
            <div className="space-y-3" data-testid="pc-preview">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><Eye className="h-3.5 w-3.5" /> Live preview (sample lead)</div>
              {pv && <><Frame html={pv.front_html} label="Front" testId="pc-preview-front" /><Frame html={pv.back_html} label="Back" testId="pc-preview-back" /></>}
              {pv?.errors?.length > 0 && <ul className="list-disc pl-5 text-xs text-red-600" data-testid="pc-errors">{pv.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
